import type { ReactNode } from "react";
import { lazy, Suspense } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";

import { SpinningLoader } from "@/components/SpinningLoader";
import { PATHS } from "@/constants/paths";
import AdminTourProvider from "@/providers/AdminTourProvider";

import { AdminNonProtectedRoute } from "./AdminNonProtectedRoute";
import { AdminProtectedRoute } from "./AdminProtectedRoute";
import { RouteErrorBoundary } from "./RouteErrorBoundary";

// NOTE: istanbul ignore on lazy factories skips the import() stub; route modules are
// covered when Playwright navigates to each admin path.

/* istanbul ignore next */
const AdminAuthProvider = lazy(async () => {
  const module = await import("@/providers/AdminAuthProvider");
  return { default: module.AdminAuthProvider };
});

const AdminSignIn = lazy(() => import("@/pages/admin/auth/SignIn"));
const AdminDashboard = lazy(() => import("@/pages/admin/dashboard/Dashboard"));
const AdminUsers = lazy(() => import("@/pages/admin/users/Users"));
const AdminUserDetail = lazy(() => import("@/pages/admin/users/UserDetail"));
const AdminOrganizations = lazy(
  () => import("@/pages/admin/organizations/Organizations"),
);
const AdminOrganizationDetail = lazy(
  () => import("@/pages/admin/organizations/OrganizationDetail"),
);
const AdminProjects = lazy(() => import("@/pages/admin/projects/Projects"));
const AdminModels = lazy(() => import("@/pages/admin/models/Models"));
const AdminModelDetail = lazy(() => import("@/pages/admin/models/ModelDetail"));
const AdminProbes = lazy(() => import("@/pages/admin/probes/Probes"));
const AdminProbeDetail = lazy(() => import("@/pages/admin/probes/ProbeDetail"));
const AdminRoles = lazy(() => import("@/pages/admin/roles/Roles"));
const AdminRoleDetail = lazy(() => import("@/pages/admin/roles/RoleDetail"));
const AdminIntegrations = lazy(
  () => import("@/pages/admin/integrations/Integrations"),
);
const AdminIntegrationDetail = lazy(
  () => import("@/pages/admin/integrations/IntegrationDetail"),
);
const AdminChats = lazy(() => import("@/pages/admin/chats/Chats"));
const AdminChatDetail = lazy(() => import("@/pages/admin/chats/ChatDetail"));
const AdminMeetings = lazy(() => import("@/pages/admin/meetings/Meetings"));
const AdminMeetingDetail = lazy(
  () => import("@/pages/admin/meetings/MeetingDetail"),
);
const AdminKnowledge = lazy(() => import("@/pages/admin/knowledge/Knowledge"));
const AdminKnowledgeDetail = lazy(
  () => import("@/pages/admin/knowledge/KnowledgeDetail"),
);
const AdminExternalApiKeys = lazy(
  () => import("@/pages/admin/external-api-keys/ExternalApiKeys"),
);
const AdminExternalApiKeyDetail = lazy(
  () => import("@/pages/admin/external-api-keys/ExternalApiKeyDetail"),
);
const AdminAuditLogs = lazy(() => import("@/pages/admin/audit-logs/AuditLogs"));
const AdminAccounts = lazy(() => import("@/pages/admin/accounts/Accounts"));
const AdminSessions = lazy(() => import("@/pages/admin/sessions/Sessions"));
const AdminUserSessions = lazy(
  () => import("@/pages/admin/user-sessions/UserSessions"),
);
const AdminSettings = lazy(() => import("@/pages/admin/settings/Settings"));
const AdminAutomations = lazy(
  () => import("@/pages/admin/automations/Automations"),
);
const AdminCronJobDetail = lazy(
  () => import("@/pages/admin/cron-jobs/CronJobDetail"),
);

/* istanbul ignore next */
const AdminActivityLog = lazy(
  () => import("@/pages/admin/activity-log/ActivityLog"),
);
/* istanbul ignore next */
const AdminSsoDomains = lazy(
  () => import("@/pages/admin/sso-domains/AdminSsoDomains"),
);
const AdminAppearance = lazy(
  () => import("@/pages/admin/appearance/AdminAppearance"),
);
/* istanbul ignore next */
const AdminAssistants = lazy(
  () => import("@/pages/admin/assistants/Assistants"),
);
/* istanbul ignore next */
const AdminAssistantDetail = lazy(
  () => import("@/pages/admin/assistants/AssistantDetail"),
);
/* istanbul ignore next */
const AdminProjectDetail = lazy(
  () => import("@/pages/admin/projects/ProjectDetail"),
);

const AdminLayout = lazy(() => import("@/pages/admin/layout/Layout"));

interface IAdminSuspenseProps {
  children: ReactNode;
}

// NOTE: NSAI-5414 — the admin tree had no error boundary at all (the regular
// app gets one per route from `RouteWrapper`), so one render-time throw on a
// page — e.g. an admin transcript rendering an attachment whose stored MIME type
// broke the file-icon lookup — unmounted the whole admin SPA to a blank page,
// and reloading the same URL crashed it again. Every level that suspends now
// also catches: a page throw keeps the admin layout and sidebar mounted and
// shows the recoverable fallback in the content area instead.
const AdminSuspense = ({ children }: IAdminSuspenseProps): ReactNode => (
  <RouteErrorBoundary>
    <Suspense
      fallback={
        <div className="flex h-screen w-screen items-center justify-center">
          <SpinningLoader />
        </div>
      }
    >
      {children}
    </Suspense>
  </RouteErrorBoundary>
);

export const AdminRoutes = (): ReactNode => {
  return (
    <AdminSuspense>
      <AdminAuthProvider>
        <Routes>
          {/* NOTE: Non-protected admin auth routes */}
          <Route
            element={
              <AdminNonProtectedRoute>
                <Outlet />
              </AdminNonProtectedRoute>
            }
          >
            <Route
              path="auth/sign-in"
              element={
                <AdminSuspense>
                  <AdminSignIn />
                </AdminSuspense>
              }
            />
          </Route>

          {/* NOTE: Protected admin routes with layout */}
          <Route
            element={
              <AdminProtectedRoute>
                <AdminTourProvider>
                  <AdminSuspense>
                    <AdminLayout />
                  </AdminSuspense>
                </AdminTourProvider>
              </AdminProtectedRoute>
            }
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route
              path="dashboard"
              element={
                <AdminSuspense>
                  <AdminDashboard />
                </AdminSuspense>
              }
            />
            <Route
              path="users"
              element={
                <AdminSuspense>
                  <AdminUsers />
                </AdminSuspense>
              }
            />
            <Route
              path="users/:uuid"
              element={
                <AdminSuspense>
                  <AdminUserDetail />
                </AdminSuspense>
              }
            />
            <Route
              path="organizations"
              element={
                <AdminSuspense>
                  <AdminOrganizations />
                </AdminSuspense>
              }
            />
            <Route
              path="organizations/:uuid"
              element={
                <AdminSuspense>
                  <AdminOrganizationDetail />
                </AdminSuspense>
              }
            />
            <Route
              path="projects"
              element={
                <AdminSuspense>
                  <AdminProjects />
                </AdminSuspense>
              }
            />
            <Route
              path="projects/:uuid"
              element={
                <AdminSuspense>
                  <AdminProjectDetail />
                </AdminSuspense>
              }
            />
            <Route
              path="models"
              element={
                <AdminSuspense>
                  <AdminModels />
                </AdminSuspense>
              }
            />
            <Route
              path="models/:uuid"
              element={
                <AdminSuspense>
                  <AdminModelDetail />
                </AdminSuspense>
              }
            />
            <Route
              path="assistants"
              element={
                <AdminSuspense>
                  <AdminAssistants />
                </AdminSuspense>
              }
            />
            <Route
              path="assistants/:uuid"
              element={
                <AdminSuspense>
                  <AdminAssistantDetail />
                </AdminSuspense>
              }
            />
            <Route
              path="probes"
              element={
                <AdminSuspense>
                  <AdminProbes />
                </AdminSuspense>
              }
            />
            <Route
              path="probes/:uuid"
              element={
                <AdminSuspense>
                  <AdminProbeDetail />
                </AdminSuspense>
              }
            />
            <Route
              path="roles"
              element={
                <AdminSuspense>
                  <AdminRoles />
                </AdminSuspense>
              }
            />
            <Route
              path="roles/:uuid"
              element={
                <AdminSuspense>
                  <AdminRoleDetail />
                </AdminSuspense>
              }
            />
            <Route
              path="integrations"
              element={
                <AdminSuspense>
                  <AdminIntegrations />
                </AdminSuspense>
              }
            />
            <Route
              path="integrations/:uuid"
              element={
                <AdminSuspense>
                  <AdminIntegrationDetail />
                </AdminSuspense>
              }
            />
            <Route
              path="chats"
              element={
                <AdminSuspense>
                  <AdminChats />
                </AdminSuspense>
              }
            />
            <Route
              path="chats/:uuid"
              element={
                <AdminSuspense>
                  <AdminChatDetail />
                </AdminSuspense>
              }
            />
            <Route
              path="meetings"
              element={
                <AdminSuspense>
                  <AdminMeetings />
                </AdminSuspense>
              }
            />
            <Route
              path="meetings/:uuid"
              element={
                <AdminSuspense>
                  <AdminMeetingDetail />
                </AdminSuspense>
              }
            />
            <Route
              path="appearance"
              element={
                <AdminSuspense>
                  <AdminAppearance />
                </AdminSuspense>
              }
            />
            <Route
              path="sso-domains"
              element={
                <AdminSuspense>
                  <AdminSsoDomains />
                </AdminSuspense>
              }
            />
            <Route
              path="knowledge"
              element={
                <AdminSuspense>
                  <AdminKnowledge />
                </AdminSuspense>
              }
            />
            <Route
              path="knowledge/:uuid"
              element={
                <AdminSuspense>
                  <AdminKnowledgeDetail />
                </AdminSuspense>
              }
            />
            <Route
              path="automations"
              element={
                <AdminSuspense>
                  <AdminAutomations />
                </AdminSuspense>
              }
            />
            <Route
              path="automations/cron-jobs"
              element={<Navigate to={PATHS.ADMIN.AUTOMATIONS} replace />}
            />
            <Route
              path="automations/cron-jobs/:uuid"
              element={
                <AdminSuspense>
                  <AdminCronJobDetail />
                </AdminSuspense>
              }
            />
            <Route
              path="automations/cron-runs"
              element={<Navigate to={PATHS.ADMIN.AUTOMATIONS} replace />}
            />
            <Route
              path="external-api-keys"
              element={
                <AdminSuspense>
                  <AdminExternalApiKeys />
                </AdminSuspense>
              }
            />
            <Route
              path="external-api-keys/:uuid"
              element={
                <AdminSuspense>
                  <AdminExternalApiKeyDetail />
                </AdminSuspense>
              }
            />
            <Route
              path="audit-logs"
              element={
                <AdminSuspense>
                  <AdminAuditLogs />
                </AdminSuspense>
              }
            />
            <Route
              path="accounts"
              element={
                <AdminSuspense>
                  <AdminAccounts />
                </AdminSuspense>
              }
            />
            <Route
              path="sessions"
              element={
                <AdminSuspense>
                  <AdminSessions />
                </AdminSuspense>
              }
            />
            <Route
              path="user-sessions"
              element={
                <AdminSuspense>
                  <AdminUserSessions />
                </AdminSuspense>
              }
            />
            <Route
              path="activity-log"
              element={
                <AdminSuspense>
                  <AdminActivityLog />
                </AdminSuspense>
              }
            />
            <Route
              path="settings"
              element={
                <AdminSuspense>
                  <AdminSettings />
                </AdminSuspense>
              }
            />
          </Route>

          {/* NOTE: Fallback - redirect to sign in */}
          <Route path="*" element={<Navigate to="auth/sign-in" replace />} />
        </Routes>
      </AdminAuthProvider>
    </AdminSuspense>
  );
};
